import { useState } from 'react';
import { useMutation, gql } from '@/apollo';
import type { Provider, ProviderFormState } from '../types/moderator.types';
import { initialProviderState } from '../types/moderator.types';
import { hasRichTextContent, normalizeRichTextHtml } from '../../../utils/richText';

// GraphQL Mutations
const CREATE_PROVIDER_FULL = gql`
  mutation CreateProviderFull($input: CreateProviderFullInput!) {
    createProviderFull(input: $input) {
      id
      firstName
      lastName
      title
      customerId
      notes {
        id
        content
        category
        order
        createdAt
        updatedAt
        createdBy
      }
    }
  }
`;

const UPDATE_PROVIDER_FULL = gql`
  mutation UpdateProviderFull($id: ID!, $input: UpdateProviderFullInput!) {
    updateProviderFull(id: $id, input: $input) {
      id
      firstName
      lastName
      title
      customerId
      notes {
        id
        content
        category
        order
        createdAt
        updatedAt
        createdBy
      }
    }
  }
`;

const SET_PROVIDER_APP_ASSIGNMENTS = gql`
  mutation SetProviderAppAssignments($supervisingProviderId: ID!, $appIds: [ID!]!) {
    setProviderAppAssignments(supervisingProviderId: $supervisingProviderId, appIds: $appIds) {
      id
    }
  }
`;

const REMOVE_PROVIDER_SUPERVISING_DOCTOR = gql`
  mutation RemoveProviderSupervisingDoctor($providerId: ID!, $supervisingProviderId: ID!) {
    removeProviderSupervisingDoctor(providerId: $providerId, supervisingProviderId: $supervisingProviderId)
  }
`;

const DELETE_PROVIDER_FULL = gql`
  mutation DeleteProviderFull($id: ID!) {
    deleteProviderFull(id: $id)
  }
`;

export interface UseProviderManagementReturn {
  // State
  showForm: boolean;
  editing: Provider | null;
  form: ProviderFormState;
  searchTerm: string;
  newNote: string;
  showDeleteModal: boolean;
  providerToDelete: string | null;
  validationError: string | null;
  
  // State setters
  setShowForm: (show: boolean) => void;
  setEditing: (provider: Provider | null) => void;
  setForm: (form: ProviderFormState | ((prev: ProviderFormState) => ProviderFormState)) => void;
  setSearchTerm: (term: string) => void;
  setNewNote: (note: string) => void;
  
  // Handlers
  handleCreate: () => void;
  handleEdit: (provider: Provider) => void;
  handleDelete: (id: string) => void;
  handleSubmit: (e: React.FormEvent, _conditions?: Array<{ id: string; name: string }>) => void;
  handleCancel: () => void;
  handleCustomerChange: (customerId: string) => void;
  handleAddNote: () => void;
  handleRemoveNote: (index: number) => void;
  handleReorderNotes: (notes: string[]) => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
  removeSupervisingDoctor: (providerId: string, supervisingProviderId: string) => Promise<void>;
  
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useProviderManagement = (
  refetchModeratorData: () => void
): UseProviderManagementReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);
  const [form, setForm] = useState<ProviderFormState>(initialProviderState);
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [createProviderFull, { loading: isCreating }] = useMutation(CREATE_PROVIDER_FULL, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialProviderState);
      refetchModeratorData();
    }
  });

  const [updateProviderFull, { loading: isUpdating }] = useMutation(UPDATE_PROVIDER_FULL, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialProviderState);
      refetchModeratorData();
    }
  });

  const [setProviderAppAssignments] = useMutation(SET_PROVIDER_APP_ASSIGNMENTS);

  const [removeProviderSupervisingDoctorMutation] = useMutation(REMOVE_PROVIDER_SUPERVISING_DOCTOR, {
    onCompleted: () => refetchModeratorData()
  });

  const removeSupervisingDoctor = async (providerId: string, supervisingProviderId: string) => {
    await removeProviderSupervisingDoctorMutation({
      variables: { providerId, supervisingProviderId }
    });
  };

  const [deleteProviderFull, { loading: isDeleting }] = useMutation(DELETE_PROVIDER_FULL, {
    onCompleted: () => {
      refetchModeratorData();
      setShowDeleteModal(false);
      setProviderToDelete(null);
    }
  });

  const handleCreate = () => {
    setShowForm(true);
    setEditing(null);
    setForm(initialProviderState);
    setValidationError(null);
  };

  const handleEdit = (provider: Provider) => {
    setShowForm(true);
    setEditing(provider);
    setForm({
      title: provider.title || '',
      firstName: provider.firstName,
      lastName: provider.lastName,
      customerId: provider.customerId || '',
      specialtyIds: provider.conditions?.map(c => c.condition.id) || [],
      phoneNumbers: provider.phoneNumbers?.map(pn => ({
        id: pn.id, // Keep id in form state for tracking
        name: pn.name,
        phone: pn.phone,
        locationId: pn.locationId || ''
      })) || [],
      maLvnName: provider.maLvnName || '',
      maLvnPhone: provider.maLvnPhone || '',
      triageNurseName: provider.triageNurseName || '',
      triageNursePhone: provider.triageNursePhone || '',
      surgerySchedulerName: provider.surgerySchedulerName || '',
      surgerySchedulerPhone: provider.surgerySchedulerPhone || '',
      practiceAdminName: provider.practiceAdminName || '',
      practiceAdminPhone: provider.practiceAdminPhone || '',
      minimumAge: provider.minimumAge ?? null,
      maximumAge: provider.maximumAge ?? null,
      ageRestrictionNotes: provider.ageRestrictionNotes || '',
      locationIds: provider.locations?.map(l => l.location.id) || [],
      insuranceIds: provider.insurances?.map(i => i.insurance.id) || [],
      languageIds: (provider as any).languages?.map((l: any) => l.language.id) || [],
      acceptsNewPatients: provider.acceptsNewPatients || false,
      requiresReferral: provider.requiresReferral || false,
      isActive: provider.isActive,
      notes: provider.notes?.map(n => n.content) || [],
      npiNumber: provider.npiNumber || '',
      schedulingInstructions: provider.schedulingInstructions || '',
      insuranceNotes: provider.insuranceNotes || '',
      supervisingProviderId: provider.supervisingProviderId || '',
      appIds: provider.apps?.map(app => app.id) || []
    });
  };

  const handleDelete = (id: string) => {
    setProviderToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (providerToDelete) {
      await deleteProviderFull({ variables: { id: providerToDelete } });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProviderToDelete(null);
  };

  const handleSubmit = async (e: React.FormEvent, _conditions?: Array<{ id: string; name: string }>) => {
    e.preventDefault();
    setValidationError(null);
    
    // Validate required fields
    if (!form.customerId) {
      setValidationError('Please select a customer');
      return;
    }
    
    if (!form.specialtyIds || form.specialtyIds.length === 0) {
      setValidationError('Please select at least one specialty');
      return;
    }
    
    // Prepare input - remove id from phoneNumbers and rename specialtyIds to conditionIds
    // Note: appIds and supervisingProviderId are handled separately (supervisingProviderId is read-only, managed via APP assignments)
    const { specialtyIds, languageIds, phoneNumbers, appIds, supervisingProviderId, ...restForm } = form;
    
    const input: any = {
      ...restForm,
      conditionIds: specialtyIds, // Map specialtyIds to conditionIds for GraphQL schema
      languageIds: languageIds || [], // Include languageIds
      phoneNumbers: phoneNumbers.map((pn: { id?: string; name: string; phone: string; locationId: string }) => ({
        name: pn.name,
        phone: pn.phone,
        locationId: pn.locationId && pn.locationId.trim() ? pn.locationId : null
      }))
      // Note: supervisingProviderId is not included here - it's managed through APP assignments
    };
    
    try {
      let providerId: string;
    if (editing) {
        await updateProviderFull({
          variables: {
            id: editing.id,
            input
          }
        });
        providerId = editing.id;
      } else {
        const result = await createProviderFull({
          variables: {
            input
          }
        });
        providerId = result.data.createProviderFull.id;
      }

      // Set APP assignments for this doctor (adds/removes links for this doctor only)
      await setProviderAppAssignments({
        variables: {
          supervisingProviderId: providerId,
          appIds: form.appIds || []
        }
      });
    } catch (error: any) {
      // Handle GraphQL errors
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'An error occurred';
      setValidationError(errorMessage);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(initialProviderState);
    setNewNote('');
    setValidationError(null);
  };

  const handleCustomerChange = (customerId: string) => {
    setForm((prev) => {
      const customerChanged = Boolean(prev.customerId && prev.customerId !== customerId);
      if (!customerChanged) {
        return { ...prev, customerId };
      }

      return {
        ...prev,
        customerId,
        locationIds: [],
        insuranceIds: [],
        appIds: [],
        phoneNumbers: prev.phoneNumbers.map((pn) => ({ ...pn, locationId: '' })),
      };
    });
    setValidationError(null);
  };

  const handleAddNote = () => {
    const content = normalizeRichTextHtml(newNote);
    if (hasRichTextContent(content)) {
      setForm({ ...form, notes: [...form.notes, content] });
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setForm({ ...form, notes: form.notes.filter((_, i) => i !== index) });
  };

  const handleReorderNotes = (notes: string[]) => {
    setForm({ ...form, notes });
  };

  return {
    showForm,
    editing,
    form,
    searchTerm,
    newNote,
    showDeleteModal,
    providerToDelete,
    validationError,
    setShowForm,
    setEditing,
    setForm,
    setSearchTerm,
    setNewNote,
    handleCreate,
    handleEdit,
    handleDelete,
    handleSubmit,
    handleCancel,
    handleCustomerChange,
    handleAddNote,
    handleRemoveNote,
    handleReorderNotes,
    confirmDelete,
    cancelDelete,
    removeSupervisingDoctor,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

